import { supabase } from '../lib/supabase';

export interface BriefTargetAudience {
  age_range: string | null;
  geo: string | null;
  purchasing_power: string | null;
  specific_traits: string[];
}

export interface BriefCreatorProfile {
  universes: string[];
  preferred_formats: string[];
  tone_keywords: string[];
  follower_range: [number, number] | null;
  min_engagement_rate: number | null;
  min_avg_views: number | null;
}

export interface BriefJson {
  brand_name: string;
  campaign_objective: string | null;
  product_focus: string | null;
  period: string | null;
  budget_range: string | null;
  target_audience: BriefTargetAudience;
  creator_profile: BriefCreatorProfile;
  roster_strategy: 'renewal' | 'consolidation' | 'mix';
  excluded_brands: string[];
  excluded_creators: string[];
  inspiration_creators: string[];
  deliverables: string | null;
  deadlines: string | null;
}

export const EMPTY_BRIEF: BriefJson = {
  brand_name: '',
  campaign_objective: null,
  product_focus: null,
  period: null,
  budget_range: null,
  target_audience: { age_range: null, geo: null, purchasing_power: null, specific_traits: [] },
  creator_profile: { universes: [], preferred_formats: [], tone_keywords: [], follower_range: null, min_engagement_rate: null, min_avg_views: null },
  roster_strategy: 'mix',
  excluded_brands: [],
  excluded_creators: [],
  inspiration_creators: [],
  deliverables: null,
  deadlines: null,
};

async function extractTextFromPdf(file: File): Promise<string> {
  const pdfjsLib = await import('pdfjs-dist');
  // Use the bundled worker
  pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url
  ).toString();

  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const texts: string[] = [];
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    texts.push(content.items.map((item: any) => item.str).join(' '));
  }
  return texts.join('\n');
}

async function extractTextFromImage(file: File): Promise<string> {
  // Convert to base64 and use OpenAI vision via edge function
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export async function extractBriefFromFile(file: File, pastedText: string): Promise<BriefJson> {
  const session = await supabase.auth.getSession();
  const token = session.data.session?.access_token;
  if (!token) throw new Error('Non authentifié');

  let textContent = pastedText.trim();
  let imageBase64: string | null = null;

  if (file) {
    if (file.type === 'application/pdf') {
      try {
        const pdfText = await extractTextFromPdf(file);
        textContent = pdfText + (textContent ? '\n\n' + textContent : '');
      } catch (e) {
        console.warn('[BRIEF-EXTRACT] PDF extraction failed, using pasted text:', e);
      }
    } else if (file.type.startsWith('image/')) {
      imageBase64 = await extractTextFromImage(file);
    }
  }

  const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/extract-brief`;
  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ text_content: textContent, image_base64: imageBase64 }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || 'Extraction échouée');
  }

  const data = await response.json();
  return { ...EMPTY_BRIEF, ...data.brief_json };
}

export interface BriefMatchResponse {
  query_analysis: any;
  brief_json: BriefJson;
  roster_strategy: string;
  sections: {
    direct: any[];
    lateral: any[];
    discovery: any[];
  };
  excluded: any[];
  all_results: any[];
  stats: any;
}

export async function matchCreatorsWithBrief(briefJson: BriefJson, castingBriefId?: string): Promise<BriefMatchResponse> {
  const session = await supabase.auth.getSession();
  const token = session.data.session?.access_token;
  if (!token) throw new Error('Non authentifié');

  const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/match-creators-brief`;
  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ brief_json: briefJson, casting_brief_id: castingBriefId || null }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    if (response.status === 402) throw new Error(err.message || 'Crédits insuffisants');
    throw new Error(err.error || 'Erreur matching');
  }

  return await response.json();
}
